// S36.1: a row's detail (D49) and its trend, the row a link brings forward open (D45, D76), and a factor's
// gauge in an open card row (Ola, 9 Oct).

// The rows a link has brought forward on this page, as "<table id>:<row id>". A table remounted by a
// new answer (its window changed) must not pull the reader back to a row they have already been shown.
const broughtForward = new Set();

// Sent on window as the reader opens or closes a row, {activity, row}: the Actions view names the open
// row in the address (&row=; insights-actions.js, ROW_EVENT).
const ROW_EVENT = "insights-activity-row";

// Whether focus is free to move: on nothing, or on an element only a script focuses (an activity's
// heading or pane, tabindex="-1", where the activity puts it as it opens), never on a control the reader
// moved to while the rows loaded.
function isFocusFree() {
    const active = typeof document === "undefined" ? null : document.activeElement;

    return !active || active === document.body || active.getAttribute?.("tabindex") === "-1";
}

// Whether the reader asked for reduced motion, under which a row is brought on screen at once.
function isMotionReduced() {
    return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
}

export default function detailPart({ HIGHLIGHT_MS, EXPAND_METHOD, trendPath }) {
    return {
        // ------------------------------------------------------------------------ a row a link named

        /**
         * The row a link named (D45's &row=, the contract's openRow, which names a row of this table or
         * is null), brought forward once per page: drawn even past the first page, scrolled to the middle
         * of the screen, highlighted for a moment, and focused, where a screen reader hears its name. A
         * row that opens (D49) is brought forward open, its trend on its way, and focused on its
         * disclosure; any other on its header.
         *
         * `again` is a link pressed after the table started (bringForwardOnRequest()): it brings the row
         * forward however often it is asked, and clears the reader's filters and search when they hide
         * it (Ali, 7 Oct), since the link named that row. `highlight` false brings it back without the
         * moment's highlight, as Back does for the row the reader left open.
         */
        bringForward(id, { again = false, highlight = true } = {}) {
            const key = `${this.table.id}:${id}`;

            if (!id || (!again && broughtForward.has(key))) {
                return;
            }

            if (again && this.rowById(id) && this.hasFilters() && !this.matched.some((row) => row.id === id)) {
                this.resetFilters();
            }

            const index = this.matched.findIndex((row) => row.id === id);

            if (index === -1) {
                return;
            }

            broughtForward.add(key);
            this.showPageOf(id);

            if (highlight) {
                clearTimeout(this.highlightTimer);
                this.highlightedRow = id;
                this.highlightTimer = setTimeout(() => {
                    this.highlightedRow = null;
                }, HIGHLIGHT_MS);
            }

            const opens = this.hasDetail(this.matched[index]);

            if (opens) {
                this.expand(id);
                this.tellOpenRow(id);
            }

            this.afterShow(() => {
                const row = this.rowElement(id);
                const target = row && ((opens && row.querySelector("[data-row-expand]")) || row.querySelector("[data-row-header]"));

                row?.scrollIntoView({ block: "center", behavior: isMotionReduced() ? "auto" : "smooth" });

                if (target && isFocusFree()) {
                    target.focus({ preventScroll: true });
                }
            });
        },

        /**
         * insights-bring-row-forward, {activity, row, highlight}: a link opened this table's activity at
         * one of its rows after the table started (a link with &row=, or Back to the row the reader
         * had open). Another activity's, or another table's row, is not this table's to bring forward.
         */
        bringForwardOnRequest({ activity, row, highlight = true } = {}) {
            if (activity !== this.activityKey() || typeof row !== "string" || !this.rowById(row)) {
                return;
            }

            this.bringForward(row, { again: true, highlight: highlight !== false });
        },

        // The activity this table belongs to, by the pane the Actions view holds it in, or null outside one.
        activityKey() {
            return this.rootElement?.closest?.("[data-insights-activity-pane]")?.dataset?.insightsActivityPane ?? null;
        },

        /**
         * The row the reader has open, told to the page, which names it in the address (&row=, D76): the
         * one just opened, or none after a close, since one row is open at a time (S36.1).
         */
        tellOpenRow(id = null) {
            const activity = this.activityKey();

            if (activity === null || typeof window === "undefined") {
                return;
            }

            const open = id ?? Object.keys(this.expanded).filter((key) => this.expanded[key] === true).at(-1) ?? null;

            window.dispatchEvent(new CustomEvent(ROW_EVENT, { detail: { activity, row: open } }));
        },

        isHighlighted(id) {
            return this.highlightedRow === id;
        },

        /**
         * The first row still to do opens as the list loads (P5, D98; the contract's opensFirstRow), so the
         * first task is on screen with no press. Not when a link already brought a row forward, and never
         * with focus or a word: the reader is where they were, and the address names no row until they
         * open one themselves.
         */
        openFirstRow() {
            if (this.table.opensFirstRow !== true || Object.keys(this.expanded).length > 0) {
                return;
            }

            const first = this.visibleRows.find((row) => !this.noticeOf(row.id) && this.hasDetail(row));

            if (first) {
                this.expand(first.id);
            }
        },

        // --------------------------------------------------------------------- a row's detail (D49)

        hasDetail(row) {
            return row.detail !== null && row.detail !== undefined;
        },

        isExpanded(id) {
            return this.expanded[id] === true;
        },

        // From the row's place on screen, as moreId() is, so it is a valid id whatever the row's own id holds.
        detailId(index) {
            return `activity-table-${this.table.id}-detail-${index}`;
        },

        // A disclosure (accessibility.md §9): the press opens or closes the detail and focus stays on it.
        // One row is open at a time (S36.1): opening one closes any other. The address follows the open
        // row, so a reload opens it again (D76).
        toggleExpand(id) {
            if (this.expanded[id]) {
                delete this.expanded[id];
                this.tellOpenRow();

                return;
            }

            this.expand(id);

            if (this.expanded[id]) {
                this.tellOpenRow(id);
            }
        },

        // Opens a row, closing any other (S36.1), whether the reader pressed it or a link brought it forward.
        expand(id) {
            const row = this.rowById(id);

            if (!row || !this.hasDetail(row)) {
                return;
            }

            this.expanded = { [id]: true };

            // A detail with no trend to draw (a target too soon to judge, S40.1) asks for none. One whose
            // Above you was answered before a read asked for since could land asks again (above-you.js).
            if ((!this.trends[id] || this.isAboveDue(id)) && row.detail.hasTrend !== false) {
                this.loadTrend(id);
            }
        },

        // The row's trend, asked for once, the first time it opens, never at load (D49), and again only
        // for a read of Google's results asked for since (isAboveDue()). A failure is said in the detail
        // with a way to ask again; everything else the detail holds is already there.
        async loadTrend(id) {
            this.trends[id] = { state: "loading", trend: null, message: "" };

            let answer;

            try {
                answer = await this.callWire(EXPAND_METHOD, [this.table.id, id]);
            } catch (error) {
                answer = null;
            }

            // The row left the list while its trend was on its way.
            if (!this.rowById(id)) {
                delete this.trends[id];

                return;
            }

            this.keepAbove(id, answer);

            const trend = answer && typeof answer === "object" ? answer.trend : null;

            if (trend && typeof trend === "object" && Array.isArray(trend.series)) {
                this.trends[id] = { state: "ready", trend, message: "" };

                return;
            }

            const message = answer && typeof answer === "object" && typeof answer.error === "string" && answer.error !== "" ? answer.error : this.strings.detail.trendFailed;

            this.trends[id] = { state: "error", trend: null, message };
        },

        // Above you (S53, D85), answered beside the trend whether or not the trend loaded: kept on the row's
        // detail, where activity-table/detail/above-you draws it from, and its menu's reads with the table
        // (keepAboveChecks()). An answer without one keeps the row's.
        keepAbove(id, answer) {
            const above = answer && typeof answer === "object" ? answer.above : null;
            const row = this.rowById(id);

            if (above && typeof above === "object" && row && row.detail) {
                row.detail.above = above;
                this.keepAboveChecks(id, above);
            }
        },

        trendOf(id) {
            return this.trends[id] || { state: "loading", trend: null, message: "" };
        },

        trendPathOf(line) {
            return trendPath(line.values, line.isLowerBetter === true);
        },

        // A line's ends: its lowest and highest ("120–480") when the activity sent its range (isRange),
        // else where it started and ended ("8.4 → 6.1").
        trendEnds(line) {
            return (line.first ?? "–") + (line.isRange ? "–" : " → ") + (line.last ?? "–");
        },

        // The heading over a row's factors: the activity's own (Win back clicks' "What changed"), else
        // "Why it ranks". No "#N": Nathan read it as Google's position (P5, D98).
        factorsTitleOf(row) {
            return row.detail.factorsTitle ?? this.strings.detail.whyItRanks;
        },
    };
}
